import { Column, FormControl, Input } from "@gadgetinc/widgets";
import React, { useEffect, useMemo, useState } from "react";
import type { BillingAccountProps } from "state-trees/src/Billing";
import { CachePolicies } from "use-http";
import { simpleEmailRegex } from "../../../client-state-trees/auth/SignUpOrSignInForm";
import { useBillingFetch } from "../Billing";
import { useBillingSession } from "./BillingContext";

export const useBillingContactForm = ({ inEditor = false }: { inEditor?: boolean } = {}) => {
  const billingSession = useBillingSession();

  const [email, setEmail] = useState(billingSession.billingAccount.email);
  const [error, setError] = useState<string | undefined>();

  const emailValid = Boolean(email && simpleEmailRegex.test(email));
  const emailChanged = email !== billingSession.billingAccount.email;

  const { loading: updatingBillingAccount, post: updateBillingAccount } = useBillingFetch<{ billingAccount: BillingAccountProps }>(
    `/auth/api/billing/billing-account/${billingSession.billingAccount.id}`,
    {
      cachePolicy: CachePolicies.NETWORK_ONLY,
    }
  );

  useEffect(() => {
    if (email.length === 0) {
      setError("Email is required");
    }
    setError(simpleEmailRegex.test(email) ? undefined : "Invalid email address");
  }, [email]);

  const submittable = useMemo(() => {
    return emailValid && emailChanged && !updatingBillingAccount;
  }, [emailValid, emailChanged, updatingBillingAccount]);

  const submit = async () => {
    setError(undefined);

    if (!emailValid) {
      setError("email is invalid");
      return { success: false };
    }

    const result: { billingAccount: BillingAccountProps } | { message: string } = await updateBillingAccount({ email });

    if ("billingAccount" in result) {
      billingSession.setBillingAccountEmail(result.billingAccount.email);
      return { success: true };
    } else {
      setError(result.message || "An error occurred updating, please try again");
      return { success: false };
    }
  };

  return {
    form: {
      email,
      setEmail,
      error,
      submittable,
    },
    submit,
    submitting: updatingBillingAccount,
  };
};

export const BillingContactForm = (props: {
  form: {
    email: string;
    setEmail: (email: string) => void;
    error: string | undefined;
    submittable: boolean;
  };
  onSubmit?: () => void | Promise<void>;
}) => {
  const { form, onSubmit } = props;

  return (
    <Column>
      <FormControl label="Email Address" caption={form.error} error={!!form.error}>
        <Input
          value={form.email}
          error={!!form.error}
          onChange={(event) => form.setEmail(event.currentTarget.value)}
          onKeyPress={(event) => {
            if (event.key === "Enter" && form.submittable && onSubmit) {
              void onSubmit();
            }
          }}
        />
      </FormControl>
    </Column>
  );
};
